import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../services/api';

interface DesignTemplate {
  filename: string;
  base64?: string;
  size_kb?: number;
}

export default function BrandKitPage() {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  // Support both new URL structure (/projects/:projectId/brand-kit) and legacy (?project_id=...)
  const projectId = urlProjectId || searchParams.get('project_id');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Logo
  const [hasLogo, setHasLogo] = useState(false);
  const [logoFilename, setLogoFilename] = useState('');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoRef = useRef<HTMLInputElement>(null);

  // Reference Thumbnail
  const [hasRefThumb, setHasRefThumb] = useState(false);
  const [refThumbFilename, setRefThumbFilename] = useState('');
  const [refThumbPreview, setRefThumbPreview] = useState<string | null>(null);
  const [refThumbUploading, setRefThumbUploading] = useState(false);
  const refThumbRef = useRef<HTMLInputElement>(null);
  const [thumbStylePrompt, setThumbStylePrompt] = useState<string | null>(null);
  const [analyzingThumb, setAnalyzingThumb] = useState(false);

  // Font
  const [hasFont, setHasFont] = useState(false);
  const [fontFilename, setFontFilename] = useState('');
  const [fontUploading, setFontUploading] = useState(false);
  const fontRef = useRef<HTMLInputElement>(null);

  // Colors
  const [colorPrimary, setColorPrimary] = useState('');
  const [colorSecondary1, setColorSecondary1] = useState('');
  const [colorSecondary2, setColorSecondary2] = useState('');

  // Design Templates
  const [projectTemplates, setProjectTemplates] = useState<DesignTemplate[]>([]);
  const [globalTemplates, setGlobalTemplates] = useState<DesignTemplate[]>([]);
  const [hasProjectTemplates, setHasProjectTemplates] = useState(false);
  const [templateUploading, setTemplateUploading] = useState(false);
  const [globalTemplateUploading, setGlobalTemplateUploading] = useState(false);
  const templateRef = useRef<HTMLInputElement>(null);
  const globalTemplateRef = useRef<HTMLInputElement>(null);
  const [globalStylePrompt, setGlobalStylePrompt] = useState<string | null>(null);
  const [analyzingGlobal, setAnalyzingGlobal] = useState(false);
  const [deletingAllGlobal, setDeletingAllGlobal] = useState(false);

  // ── Load Brand Kit (Fast - No Images) ──────────────────────────────────
  const loadBrandKit = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      // Load metadata only (fast)
      const res = await api.get(`/brandkit/${projectId}`);
      const d = res.data;
      setHasLogo(d.has_logo);
      setLogoFilename(d.logo_filename || '');
      setHasRefThumb(d.has_reference_thumbnail);
      setRefThumbFilename(d.reference_thumbnail_filename || '');
      setHasFont(d.has_font);
      setFontFilename(d.font_filename || '');
      setColorPrimary(d.color_primary || '');
      setColorSecondary1(d.color_secondary1 || '');
      setColorSecondary2(d.color_secondary2 || '');
      setProjectTemplates(d.design_templates || []);
      setHasProjectTemplates(d.has_project_templates || false);
      setThumbStylePrompt(d.thumbnail_style_prompt || null);

      // Lazy-load images in background
      if (d.has_logo) {
        api.get(`/brandkit/${projectId}/logo/preview`)
          .then(r => setLogoPreview(`data:image/png;base64,${r.data.base64}`))
          .catch(() => {});
      }
      if (d.has_reference_thumbnail) {
        api.get(`/brandkit/${projectId}/reference-thumbnail/preview`)
          .then(r => setRefThumbPreview(`data:image/png;base64,${r.data.base64}`))
          .catch(() => {});
      }
    } catch { /* No brand kit yet */ }
    setLoading(false);
  }, [projectId]);

  const loadGlobalTemplates = useCallback(async () => {
    try {
      const res = await api.get('/brandkit/global/design-templates');
      setGlobalTemplates(res.data.templates || []);
      setGlobalStylePrompt(res.data.style_prompt || null);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadBrandKit(); loadGlobalTemplates(); }, [loadBrandKit, loadGlobalTemplates]);

  const showSuccess = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  // ── Upload Handlers ─────────────────────────────────────────────────────
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !projectId) return;
    setLogoUploading(true); setError('');
    try {
      const fd = new FormData(); fd.append('file', file);
      await api.post(`/brandkit/${projectId}/logo`, fd);
      await loadBrandKit();
      showSuccess('Logo uploaded');
    } catch (err: any) { setError(err.response?.data?.detail || 'Upload failed'); }
    setLogoUploading(false);
    if (logoRef.current) logoRef.current.value = '';
  };

  const handleRefThumbUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !projectId) return;
    setRefThumbUploading(true); setError('');
    try {
      const fd = new FormData(); fd.append('file', file);
      await api.post(`/brandkit/${projectId}/reference-thumbnail`, fd);
      await loadBrandKit();
      showSuccess('Reference thumbnail uploaded — style analysis running in background...');
    } catch (err: any) { setError(err.response?.data?.detail || 'Upload failed'); }
    setRefThumbUploading(false);
    if (refThumbRef.current) refThumbRef.current.value = '';
  };

  const handleFontUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !projectId) return;
    setFontUploading(true); setError('');
    try {
      const fd = new FormData(); fd.append('file', file);
      await api.post(`/brandkit/${projectId}/font`, fd);
      await loadBrandKit();
      showSuccess('Font uploaded');
    } catch (err: any) { setError(err.response?.data?.detail || 'Upload failed'); }
    setFontUploading(false);
    if (fontRef.current) fontRef.current.value = '';
  };

  const handleTemplateUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !projectId) return;
    setTemplateUploading(true); setError('');
    let uploaded = 0;
    try {
      for (let i = 0; i < files.length; i++) {
        const fd = new FormData(); fd.append('file', files[i]);
        await api.post(`/brandkit/${projectId}/design-templates`, fd);
        uploaded++;
      }
      await loadBrandKit();
      showSuccess(`${uploaded} design template${uploaded > 1 ? 's' : ''} uploaded`);
    } catch (err: any) { setError(err.response?.data?.detail || `Upload failed (${uploaded}/${files.length} uploaded)`); }
    setTemplateUploading(false);
    if (templateRef.current) templateRef.current.value = '';
  };

  const handleGlobalTemplateUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setGlobalTemplateUploading(true); setError('');
    let uploaded = 0;
    try {
      for (let i = 0; i < files.length; i++) {
        const fd = new FormData(); fd.append('file', files[i]);
        await api.post('/brandkit/global/design-templates', fd);
        uploaded++;
      }
      await loadGlobalTemplates();
      showSuccess(`${uploaded} global template${uploaded > 1 ? 's' : ''} uploaded — style analysis running in background...`);
    } catch (err: any) { setError(err.response?.data?.detail || `Upload failed (${uploaded}/${files.length} uploaded)`); }
    setGlobalTemplateUploading(false);
    if (globalTemplateRef.current) globalTemplateRef.current.value = '';
  };

  // ── Save Colors ─────────────────────────────────────────────────────────
  const handleSaveColors = async () => {
    if (!projectId) return;
    setSaving(true); setError('');
    try {
      await api.put(`/brandkit/${projectId}/colors`, {
        color_primary: colorPrimary || null,
        color_secondary1: colorSecondary1 || null,
        color_secondary2: colorSecondary2 || null,
      });
      showSuccess('Colors saved');
    } catch (err: any) { setError(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  // ── Delete Handlers ─────────────────────────────────────────────────────
  const handleDeleteLogo = async () => {
    if (!projectId) return;
    try { await api.delete(`/brandkit/${projectId}/logo`); setHasLogo(false); setLogoPreview(null); setLogoFilename(''); } catch {}
  };
  const handleDeleteRefThumb = async () => {
    if (!projectId) return;
    try { await api.delete(`/brandkit/${projectId}/reference-thumbnail`); setHasRefThumb(false); setRefThumbPreview(null); setRefThumbFilename(''); setThumbStylePrompt(null); } catch {}
  };
  const handleDeleteFont = async () => {
    if (!projectId) return;
    try { await api.delete(`/brandkit/${projectId}/font`); setHasFont(false); setFontFilename(''); } catch {}
  };
  const handleDeleteTemplate = async (index: number) => {
    if (!projectId) return;
    try { await api.delete(`/brandkit/${projectId}/design-templates/${index}`); await loadBrandKit(); } catch {}
  };
  const handleDeleteGlobalTemplate = async (filename: string) => {
    try { await api.delete(`/brandkit/global/design-templates/${filename}`); await loadGlobalTemplates(); } catch {}
  };
  const handleDeleteAllGlobalTemplates = async () => {
    if (!confirm('Delete ALL global design templates?')) return;
    setDeletingAllGlobal(true);
    try {
      await api.delete('/brandkit/global/design-templates');
      setGlobalTemplates([]);
      setGlobalStylePrompt(null);
      showSuccess('All global templates deleted');
    } catch (err: any) { setError(err.response?.data?.detail || 'Delete failed'); }
    setDeletingAllGlobal(false);
  };

  // ── Analyze Style Handlers ──────────────────────────────────────────────
  const handleAnalyzeThumbStyle = async () => {
    if (!projectId) return;
    setAnalyzingThumb(true); setError('');
    try {
      const res = await api.post(`/brandkit/${projectId}/analyze-thumbnail-style`);
      setThumbStylePrompt(res.data.style_prompt);
      showSuccess('Thumbnail style analyzed');
    } catch (err: any) { setError(err.response?.data?.detail || 'Analysis failed'); }
    setAnalyzingThumb(false);
  };

  const handleAnalyzeGlobalStyle = async () => {
    setAnalyzingGlobal(true); setError('');
    try {
      const res = await api.post('/brandkit/global/analyze-style');
      setGlobalStylePrompt(res.data.style_prompt);
      showSuccess('Global infographic style analyzed');
    } catch (err: any) { setError(err.response?.data?.detail || 'Analysis failed'); }
    setAnalyzingGlobal(false);
  };

  if (!projectId) {
    return (
      <Layout><div className="p-6"><p className="text-gray-500">Please select a project first.</p></div></Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Brand Kit</h1>
        <p className="text-sm text-gray-500 mb-6">
          Upload brand assets for this project. These are used when generating images (thumbnails, infographics, CP Images).
        </p>

        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}
        {success && <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">{success}</div>}

        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading brand kit...</div>
        ) : (
          <div className="space-y-6">

            {/* ── Logo ── */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-800 mb-1">Logo</h2>
              <p className="text-xs text-gray-400 mb-3">PNG format. Appears on top-left corner of all generated images.</p>
              {hasLogo && logoPreview ? (
                <div className="flex items-center gap-4 mb-3">
                  <img src={logoPreview} alt="Logo" className="max-h-12 max-w-[150px] object-contain bg-gray-100 rounded p-1" />
                  <div>
                    <span className="text-xs text-gray-500">{logoFilename}</span>
                    <button onClick={handleDeleteLogo} className="ml-3 text-xs text-red-500 hover:text-red-700">Remove</button>
                  </div>
                </div>
              ) : null}
              <input ref={logoRef} type="file" accept=".png" onChange={handleLogoUpload} className="hidden" />
              <button onClick={() => logoRef.current?.click()} disabled={logoUploading}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {logoUploading ? 'Uploading...' : hasLogo ? 'Replace Logo' : 'Upload Logo (PNG)'}
              </button>
            </div>

            {/* ── Reference Thumbnail ── */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-800 mb-1">Reference Thumbnail</h2>
              <p className="text-xs text-gray-400 mb-3">Upload a sample thumbnail as style reference. AI will analyze and generate similar thumbnails.</p>
              {hasRefThumb && refThumbPreview ? (
                <div className="mb-3">
                  <div className="flex items-center gap-4 mb-2">
                    <img src={refThumbPreview} alt="Reference" className="max-h-24 max-w-[200px] object-contain bg-gray-100 rounded p-1" />
                    <div>
                      <span className="text-xs text-gray-500">{refThumbFilename}</span>
                      <button onClick={handleDeleteRefThumb} className="ml-3 text-xs text-red-500 hover:text-red-700">Remove</button>
                    </div>
                  </div>
                  {thumbStylePrompt ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-blue-700">AI Style Analysis</span>
                        <button onClick={handleAnalyzeThumbStyle} disabled={analyzingThumb}
                          className="text-[10px] px-2 py-0.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition-colors disabled:opacity-50">
                          {analyzingThumb ? 'Analyzing...' : 'Re-analyze'}
                        </button>
                      </div>
                      <p className="text-xs text-blue-800">{thumbStylePrompt}</p>
                    </div>
                  ) : (
                    <button onClick={handleAnalyzeThumbStyle} disabled={analyzingThumb}
                      className="px-3 py-1.5 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors disabled:opacity-50 mb-2">
                      {analyzingThumb ? 'Analyzing...' : 'Analyze Style with AI'}
                    </button>
                  )}
                </div>
              ) : null}
              <input ref={refThumbRef} type="file" accept=".png,.jpg,.jpeg,.webp" onChange={handleRefThumbUpload} className="hidden" />
              <button onClick={() => refThumbRef.current?.click()} disabled={refThumbUploading}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {refThumbUploading ? 'Uploading...' : hasRefThumb ? 'Replace Thumbnail' : 'Upload Reference Thumbnail'}
              </button>
            </div>

            {/* ── Font ── */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-800 mb-1">Brand Font</h2>
              <p className="text-xs text-gray-400 mb-3">Optional. TTF/OTF/WOFF font file used for text overlays on images.</p>
              {hasFont ? (
                <div className="flex items-center gap-3 mb-3">
                  <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="text-sm text-gray-700">{fontFilename}</span>
                  <button onClick={handleDeleteFont} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                </div>
              ) : null}
              <input ref={fontRef} type="file" accept=".ttf,.otf,.woff,.woff2" onChange={handleFontUpload} className="hidden" />
              <button onClick={() => fontRef.current?.click()} disabled={fontUploading}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {fontUploading ? 'Uploading...' : hasFont ? 'Replace Font' : 'Upload Font'}
              </button>
            </div>

            {/* ── Brand Colors ── */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-800 mb-1">Brand Colors</h2>
              <p className="text-xs text-gray-400 mb-4">Hex values used for text and accents on generated images.</p>
              <div className="grid grid-cols-3 gap-4 mb-4">
                {[
                  { label: 'Primary Color', value: colorPrimary, set: setColorPrimary },
                  { label: 'Secondary Color 1', value: colorSecondary1, set: setColorSecondary1 },
                  { label: 'Secondary Color 2', value: colorSecondary2, set: setColorSecondary2 },
                ].map(({ label, value, set }) => (
                  <div key={label}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={value || '#000000'} onChange={e => set(e.target.value)}
                        className="w-10 h-10 rounded cursor-pointer border border-gray-300" />
                      <input type="text" value={value} onChange={e => set(e.target.value)} placeholder="#1A2B3C"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                  </div>
                ))}
              </div>
              {(colorPrimary || colorSecondary1 || colorSecondary2) && (
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs text-gray-500">Preview:</span>
                  {colorPrimary && <div className="w-8 h-8 rounded-full border border-gray-300" style={{ backgroundColor: colorPrimary }} title={colorPrimary} />}
                  {colorSecondary1 && <div className="w-8 h-8 rounded-full border border-gray-300" style={{ backgroundColor: colorSecondary1 }} title={colorSecondary1} />}
                  {colorSecondary2 && <div className="w-8 h-8 rounded-full border border-gray-300" style={{ backgroundColor: colorSecondary2 }} title={colorSecondary2} />}
                </div>
              )}
              <button onClick={handleSaveColors} disabled={saving}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save Colors'}
              </button>
            </div>

            {/* ── Design Templates (Project-Specific) ── */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-800 mb-1">Design Templates <span className="text-xs font-normal text-gray-400">(this project)</span></h2>
              <p className="text-xs text-gray-400 mb-3">
                Upload reference design images for this project. These override the global templates below. AI analyzes their style for image generation.
              </p>
              {projectTemplates.length > 0 && (
                <div className="flex flex-wrap gap-3 mb-3">
                  {projectTemplates.map((t, i) => (
                    <div key={i} className="relative group">
                      {t.base64 && <img src={`data:image/png;base64,${t.base64}`} alt={t.filename} className="h-24 rounded border border-gray-200 object-contain" />}
                      <div className="text-xs text-gray-500 mt-1 truncate max-w-[120px]">{t.filename}</div>
                      <button onClick={() => handleDeleteTemplate(i)}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        x
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <input ref={templateRef} type="file" accept=".png,.jpg,.jpeg,.webp" multiple onChange={handleTemplateUpload} className="hidden" />
              <button onClick={() => templateRef.current?.click()} disabled={templateUploading}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {templateUploading ? 'Uploading...' : '+ Add Design Templates'}
              </button>
              {!hasProjectTemplates && <span className="text-xs text-gray-400 ml-3">Using global templates (below)</span>}
            </div>

            {/* ── Global Design Templates ── */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-sm font-semibold text-gray-800">Global Design Templates</h2>
                <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">shared across all projects</span>
              </div>
              <p className="text-xs text-gray-400 mb-3">
                Default design templates used for all projects that don't have their own. AI analyzes uploaded images to create a style guide for infographic generation.
              </p>
              {globalTemplates.length > 0 && (
                <div className="flex flex-wrap gap-3 mb-3">
                  {globalTemplates.map((t, i) => (
                    <div key={i} className="relative group">
                      {t.base64 && <img src={`data:image/png;base64,${t.base64}`} alt={t.filename} className="h-24 rounded border border-gray-200 object-contain" />}
                      <div className="text-xs text-gray-500 mt-1 truncate max-w-[120px]">{t.filename}</div>
                      <button onClick={() => handleDeleteGlobalTemplate(t.filename)}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        x
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Style Analysis Display */}
              {globalStylePrompt && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-purple-700">AI Style Analysis</span>
                    <button onClick={handleAnalyzeGlobalStyle} disabled={analyzingGlobal}
                      className="text-[10px] px-2 py-0.5 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded transition-colors disabled:opacity-50">
                      {analyzingGlobal ? 'Analyzing...' : 'Re-analyze'}
                    </button>
                  </div>
                  <p className="text-xs text-purple-800">{globalStylePrompt}</p>
                </div>
              )}

              {globalTemplates.length === 0 && <p className="text-xs text-gray-400 mb-3">No global templates uploaded yet.</p>}

              <div className="flex items-center gap-2">
                <input ref={globalTemplateRef} type="file" accept=".png,.jpg,.jpeg,.webp" multiple onChange={handleGlobalTemplateUpload} className="hidden" />
                <button onClick={() => globalTemplateRef.current?.click()} disabled={globalTemplateUploading}
                  className="px-4 py-2 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50">
                  {globalTemplateUploading ? 'Uploading...' : '+ Add Global Templates'}
                </button>

                {globalTemplates.length > 0 && !globalStylePrompt && (
                  <button onClick={handleAnalyzeGlobalStyle} disabled={analyzingGlobal}
                    className="px-3 py-2 text-sm bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg transition-colors disabled:opacity-50">
                    {analyzingGlobal ? 'Analyzing...' : 'Analyze Style with AI'}
                  </button>
                )}

                {globalTemplates.length > 0 && (
                  <button onClick={handleDeleteAllGlobalTemplates} disabled={deletingAllGlobal}
                    className="px-3 py-2 text-sm bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors disabled:opacity-50">
                    {deletingAllGlobal ? 'Deleting...' : 'Delete All'}
                  </button>
                )}
              </div>
            </div>

          </div>
        )}
      </div>
    </Layout>
  );
}
